import { Opportunity, OppStage, PostalAddress } from '../../models';
import { OPPORTUNITIES, OPP_STAGES } from '../prototype/opportunities-data';

/**
 * Funnel stages — from the prototype OPP_STAGES.
 *
 * The prototype's `color` is a free hex, and a stage now wears a swatch of the
 * company palette by `colorGuid`. The palette only exists on the server, so the
 * seed cannot name one: a made-up guid would be a colour the palette has never
 * heard of, and keeping the hex would be a second palette. The stages carry
 * `colorGuid: null` and draw in the shared fallback until a colour is chosen in
 * the stage editor. (The backend's own seed maps them — Lead → Slate, Qualified
 * → Sky, Bid Submitted → Amber, Awarded → Emerald, Lost → Rose.)
 */
export const OPP_STAGES_SEED: readonly OppStage[] = (OPP_STAGES as any[]).map(
  (s): OppStage => ({
    id: s.id,
    label: s.label ?? '',
    hint: s.hint ?? '',
    colorGuid: null,
  }),
);

/** Funnel opportunities — from the prototype OPPORTUNITIES. */
export const OPPORTUNITIES_SEED: readonly Opportunity[] = (OPPORTUNITIES as any[]).map(
  (o): Opportunity => ({
    id: o.id,
    name: o.name ?? '',
    client: o.client ?? '',
    value: o.value ?? '',
    stage: o.stage ?? 'lead',
    owner: o.owner ?? '',
    ownerId: o.ownerId,
    date: o.date ?? '',
    createdAt: o.createdAt,
    note: o.note ?? '',
    lostReason: o.lostReason ?? '',
    wonReason: o.wonReason ?? '',
    contactName: o.contactName ?? '',
    contactPhone: o.contactPhone ?? '',
    contactEmail: o.contactEmail ?? '',
    siteAddress: parseSiteAddress(o.siteAddress),
    nextSteps: Array.isArray(o.nextSteps)
      ? o.nextSteps.map((s: any) => ({ id: s.id, text: s.text ?? '', due: s.due ?? '', done: !!s.done }))
      : [],
  }),
);

/**
 * The prototype records a job site as one line — "820 Maple St, Fairview". The
 * model holds a structured address, so the seed splits it rather than the
 * content being rewritten: everything before the last comma is the street, what
 * follows is the city. State and ZIP were never in the prototype's text and are
 * left empty rather than invented.
 */
function parseSiteAddress(raw: unknown): PostalAddress {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) return { street: '', city: '', state: '', postalCode: '' };
  const at = text.lastIndexOf(',');
  return at < 0
    ? { street: text, city: '', state: '', postalCode: '' }
    : { street: text.slice(0, at).trim(), city: text.slice(at + 1).trim(), state: '', postalCode: '' };
}
